import React, { useContext, useMemo, useState } from 'react';
import {
  Linking,
  Platform,
  Share,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';
import { Menu, Text } from 'react-native-paper';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';

import JobsContext from '../context/JobsContext';
import { JobType } from '../types';
import { cleanInlineText } from '../utils';
import { computeFitScore, getPrimaryResume } from '../lib/match/fitScore';
import { Palette, radius, shadow, spacing, useThemePalette } from '../theme';
import { recordError, track } from '../lib/analytics';
import Description from './Description';
import ModalSheet from './ModalSheet';

const JobStructuredData: React.FC<{ job: JobType }> = ({ job }) => {
  if (Platform.OS !== 'web') return null;

  const schema = {
    '@context': 'https://schema.org/',
    '@type': 'JobPosting',
    title: cleanInlineText(job.position),
    description: job.description,
    hiringOrganization: {
      '@type': 'Organization',
      name: cleanInlineText(job.company),
    },
    jobLocation: {
      '@type': 'Place',
      address: { '@type': 'PostalAddress', addressCountry: 'Remote' },
    },
    employmentType: 'REMOTE',
    datePosted: job.date,
    url: job.url,
    ...(job.salary && { baseSalary: job.salary }),
    ...(job.tags && { skills: job.tags.join(', ') }),
  };

  return React.createElement('script', {
    type: 'application/ld+json',
    dangerouslySetInnerHTML: { __html: JSON.stringify(schema) },
  });
};

interface JobProps {
  data: JobType;
}

const Job: React.FC<JobProps> = ({ data }) => {
  const { keys, applied, resumes, handleFavorites, handleApplied } = useContext(JobsContext);
  const { t } = useTranslation();
  const palette = useThemePalette();
  const styles = useMemo(() => makeStyles(palette), [palette]);
  const [menuVisible, setMenuVisible] = useState(false);
  const [showDescription, setShowDescription] = useState(false);

  // Fit of this job vs. the user's primary resume. Computed per row, but the
  // FlatList only renders a handful of rows at a time, so we never score the
  // whole 5k-job list up front. Null when the user has no resume.
  const primaryResume = useMemo(() => getPrimaryResume(resumes), [resumes]);
  const fit = useMemo(
    () => (primaryResume ? computeFitScore(primaryResume, data) : null),
    [primaryResume, data]
  );

  const {
    id,
    url,
    position: rawPosition,
    tags,
    salary: rawSalary,
    company: rawCompany,
    description,
    date,
  } = data;
  // Decode entities / repair mojibake for display ("R&amp;D" → "R&D"). Done
  // here at the source so the card, the Description modal, Salary, ApplyBrowser,
  // share text and analytics all get clean values.
  const position = useMemo(() => cleanInlineText(rawPosition), [rawPosition]);
  const company = useMemo(() => cleanInlineText(rawCompany), [rawCompany]);
  const salary = useMemo(() => cleanInlineText(rawSalary), [rawSalary]);

  const isFavorite = keys.includes(id);
  const isApplied = applied.includes(id);

  const dateFormatted = new Date(date).toUTCString().slice(5, 16);

  const closeMenu = () => setMenuVisible(false);

  const handleSharing = () => {
    Share.share(
      {
        message: t('share.message', { position, company, url }),
        url,
        title: t('share.title', { position, company }),
      },
      {
        subject: t('share.subject'),
        dialogTitle: t('share.dialogTitle'),
        tintColor: palette.primary,
      }
    );
  };

  const handleOpenUrl = () => {
    if (handleApplied && !isApplied) {
      handleApplied(data);
      track('applied_toggle', { jobId: id, source: 'open_url', value: true });
    }
    track('job_apply', { jobId: id, company });
    Linking.openURL(url).catch((err) => recordError(err, `open_url:${id}`));
  };

  const toggleFavorite = () => {
    if (!handleFavorites) return;
    handleFavorites(data);
    track('favorite_toggle', { jobId: id, value: !isFavorite });
  };

  const toggleApplied = () => {
    if (!handleApplied) return;
    handleApplied(data);
    track('applied_toggle', { jobId: id, source: 'menu', value: !isApplied });
  };

  const openDescription = () => {
    track('job_view', { jobId: id, company });
    setShowDescription(true);
  };

  const wrap = (fn: () => void) => () => {
    closeMenu();
    fn();
  };

  return (
    <View style={styles.cardWrapper}>
      <JobStructuredData job={data} />
      <TouchableOpacity
        activeOpacity={0.85}
        onPress={openDescription}
        style={styles.card}
      >
        <View style={styles.headerRow}>
          <View style={styles.headerText}>
            <Text style={styles.title} numberOfLines={2}>
              {position || ''}
            </Text>
            <Text style={styles.company} numberOfLines={1}>
              {company}
            </Text>
          </View>
          <TouchableOpacity
            onPress={toggleFavorite}
            hitSlop={8}
            style={styles.iconBtn}
            accessibilityLabel={
              isFavorite ? t('icons.removeFromFavorites') : t('icons.addToFavorites')
            }
          >
            <MaterialCommunityIcons
              name={isFavorite ? 'heart' : 'heart-outline'}
              size={22}
              color={isFavorite ? palette.favorite : palette.textMuted}
            />
          </TouchableOpacity>
          <Menu
            visible={menuVisible}
            onDismiss={closeMenu}
            anchorPosition="bottom"
            contentStyle={styles.menuContent}
            anchor={
              <TouchableOpacity
                onPress={() => setMenuVisible(true)}
                hitSlop={8}
                style={styles.iconBtn}
                accessibilityLabel={t('icons.jobActions')}
              >
                <MaterialCommunityIcons
                  name="dots-vertical"
                  size={22}
                  color={palette.textMuted}
                />
              </TouchableOpacity>
            }
          >
            <Menu.Item
              leadingIcon={isApplied ? 'delete-outline' : 'check-circle-outline'}
              onPress={wrap(toggleApplied)}
              title={t(isApplied ? 'icons.removeApplied' : 'icons.markApplied')}
              titleStyle={isApplied ? styles.menuItemDestructive : styles.menuItemTitle}
            />
            {Platform.OS !== 'web' && (
              <Menu.Item
                leadingIcon="share-variant"
                onPress={wrap(handleSharing)}
                title={t('icons.share')}
                titleStyle={styles.menuItemTitle}
              />
            )}
            <Menu.Item
              leadingIcon="open-in-new"
              onPress={wrap(handleOpenUrl)}
              title={t('icons.openUrl')}
              titleStyle={styles.menuItemTitle}
            />
          </Menu>
        </View>

        <View style={styles.footerRow}>
          <View style={styles.footerLeft}>
            <View style={styles.datePill}>
              <Text style={styles.date}>{dateFormatted}</Text>
            </View>
            {fit && fit.score > 0 && (
              <View style={styles.fitPill}>
                <MaterialCommunityIcons
                  name="target"
                  size={12}
                  color={palette.appliedText}
                />
                <Text style={styles.fitText} numberOfLines={1}>
                  {t('job.fit', { score: fit.score })}
                  {fit.matched.length > 0 ? ` · ${fit.matched.slice(0, 2).join(', ')}` : ''}
                </Text>
              </View>
            )}
          </View>
          {isApplied && (
            <View style={styles.appliedBadge}>
              <MaterialCommunityIcons
                name="check-circle"
                size={14}
                color={palette.appliedText}
              />
              <Text style={styles.appliedText}>{t('job.applied')}</Text>
            </View>
          )}
        </View>
      </TouchableOpacity>

      <ModalSheet
        visible={showDescription}
        onDismiss={() => setShowDescription(false)}
        cardStyle={styles.descriptionCard}
      >
        <Description
          description={description}
          tags={tags}
          salary={salary}
          date={date}
          url={url}
          company={company}
          position={position}
          onClose={() => setShowDescription(false)}
        />
      </ModalSheet>
    </View>
  );
};

const makeStyles = (p: Palette) =>
  StyleSheet.create({
    cardWrapper: {
      paddingHorizontal: spacing.lg,
      paddingTop: spacing.md,
    },
    card: {
      backgroundColor: p.surface,
      borderRadius: radius.md,
      paddingHorizontal: spacing.lg,
      paddingVertical: spacing.md,
      ...shadow.card,
    },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
    },
    headerText: { flex: 1, paddingRight: spacing.sm },
    title: { fontSize: 16, fontWeight: '700', color: p.textPrimary },
    company: { fontSize: 13, color: p.textSecondary, marginTop: 2 },
    iconBtn: { padding: spacing.xs, marginLeft: spacing.xs },
    footerRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: spacing.md,
    },
    datePill: {
      backgroundColor: p.surfaceMuted,
      paddingHorizontal: spacing.sm,
      paddingVertical: 4,
      borderRadius: radius.sm,
    },
    date: { fontSize: 12, color: p.textSecondary },
    footerLeft: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      marginRight: spacing.sm,
    },
    fitPill: {
      flexShrink: 1,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      backgroundColor: p.surfaceMuted,
      paddingHorizontal: spacing.sm,
      paddingVertical: 4,
      borderRadius: radius.sm,
    },
    fitText: { fontSize: 11, color: p.textSecondary, fontWeight: '600', flexShrink: 1 },
    appliedBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      backgroundColor: p.appliedBg,
      paddingHorizontal: spacing.sm,
      paddingVertical: 4,
      borderRadius: radius.pill,
    },
    appliedText: { fontSize: 11, color: p.appliedText, fontWeight: '600' },
    descriptionCard: {
      maxWidth: 720,
      height: '80%',
      borderRadius: radius.md,
      backgroundColor: p.surface,
    },
    menuContent: {
      backgroundColor: p.surface,
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: p.border,
    },
    menuItemTitle: { color: p.textPrimary, fontSize: 14 },
    menuItemDestructive: { color: p.danger, fontSize: 14 },
  });

export default Job;
